import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GigType } from "model/Gig";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { InputField } from "components/designV2/inputfield/inputfield";
import { TextArea } from "components/designV2/textarea/textarea";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { MultiSelectOption } from "../../../general/multiSelectWithLabel/multiSelectWithLabel.component";
import { FormData, RecruitFormData } from "../modal/gigFormModal.types";
import { GigSkills } from "./gigSkills.component";
import { WorkerCounter } from "../workerCounter/workerCounter.component";
import {
  DescriptionFieldWrap,
  RecruitIntroCard,
  RecruitTopRow,
} from "../createGigModal.styled";

type Props = {
  recruitFormData: RecruitFormData;
  setRecruitFormData: React.Dispatch<React.SetStateAction<RecruitFormData>>;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  formData: FormData;
  havePublicModule: boolean;
  haveTeamModule: boolean;
};

export const RecruitGigForm: React.FC<Props> = (props) => {
  const ROLE_MAX_LENGTH = 24;
  const { t } = useTranslation();
  const [state] = useOrganizationalStructureStore();
  const {
    formData,
    havePublicModule,
    haveTeamModule,
    setFormData,
    recruitFormData,
    setRecruitFormData,
  } = props;

  const showTypeToggle = havePublicModule && haveTeamModule;

  useEffect(() => {
    if (!showTypeToggle) {
      if (havePublicModule && formData.type !== GigType.PUBLIC) {
        setFormData((previousFormData) => ({
          ...previousFormData,
          type: GigType.PUBLIC,
        }));
      } else if (haveTeamModule && formData.type !== GigType.WORKPOOL) {
        setFormData((previousFormData) => ({
          ...previousFormData,
          type: GigType.WORKPOOL,
        }));
      }
    }
  }, [
    showTypeToggle,
    havePublicModule,
    haveTeamModule,
    formData.type,
    setFormData,
  ]);

  const teamOptions: MultiSelectOption[] = useMemo(() => {
    return (state.structure?.entities ?? [])
      .flatMap((entity) => entity.units ?? [])
      .filter((unit) => unit.name !== "Unhandled")
      .flatMap((unit) =>
        (unit.workGroups ?? []).map((team) => ({
          value: String(team.id),
          label: `${unit.name} - ${team.name}`,
        })),
      );
  }, [state.structure]);

  const isPublic = formData.type === GigType.PUBLIC;

  const selectedTeamValue =
    recruitFormData.workGroupId != null
      ? String(recruitFormData.workGroupId)
      : "";

  return (
    <>
      <RecruitIntroCard>
        <Text typography="h5" color={ColorV2.Blue1}>
          {t("CreateShift.AboutShift")}
        </Text>
        <RecruitTopRow>
          <Dropdown
            label={t("CreateShift.Team") + "*"}
            options={teamOptions}
            value={selectedTeamValue}
            onChange={(val: string) =>
              setRecruitFormData({
                ...recruitFormData,
                workGroupId: val ? Number(val) : undefined,
              })
            }
            placeholder={t("CreateShift.ChooseTeam")}
            fullWidth={false}
          />

          <InputField
            label={t("CreateShift.RoleWanted") + "*"}
            value={recruitFormData.role}
            onChange={(role) =>
              setRecruitFormData((previousFormData) => ({
                ...previousFormData,
                role,
              }))
            }
            placeholder={t("CreateShift.RoleWantedPlaceholder")}
            maxLength={ROLE_MAX_LENGTH}
            hintText={`${recruitFormData.role.length}/${ROLE_MAX_LENGTH}`}
          />

          <WorkerCounter
            label={t("CreateShift.WorkersNeeded")}
            value={recruitFormData.amountOfWorkers}
            onChange={(val: number) =>
              setRecruitFormData((previousFormData) => ({
                ...previousFormData,
                amountOfWorkers: val,
              }))
            }
          />
        </RecruitTopRow>

        <DescriptionFieldWrap>
          <TextArea
            label={t("CreateShift.ShiftDescription")}
            value={formData.description}
            onChange={(description) =>
              setFormData((previousFormData) => ({
                ...previousFormData,
                description,
              }))
            }
            placeholder={t("CreateShift.ShiftDescriptionPlaceholder")}
            maxLength={500}
          />
        </DescriptionFieldWrap>

        {isPublic && (
          <GigSkills
            formData={recruitFormData}
            setFormData={setRecruitFormData}
          />
        )}
      </RecruitIntroCard>
    </>
  );
};
